/**
 * InstagramAppDownloadReel — versione 9:16 (1080×1920) della card "Scarica
 * l'app". Pensata per Instagram Reel cover / Story.
 *
 * iter v32 r48 — User M1453 · «Dovevi farlo uguale senza cambiare. Solo
 * adattare al formato reel senza bande».
 *
 * Direzione di design: COPIA LETTERALE del 4:5 (`InstagramAppDownload`)
 * con gli stessi contenuti, animazioni e tipografie. L'unica differenza è
 * che il canvas è 1080×1920 (570px più alto) e i 570px extra sono
 * distribuiti in maniera naturale come "aria" extra in alto e in basso:
 *   · header push da top=54 → top=120  (+66 di respiro sopra)
 *   · divider top=126 → top=196
 *   · eyebrow top=170 → top=260
 *   · titolo+sub-claim+linea+body top=210 → top=320 (+110)
 *   · bullet 9 servizi top=590 → top=820 (+230, più aria con telefono)
 *   · iPhone top=340 → top=560 (+220, scende come il resto)
 *   · footer bottom=50 → bottom=130 (+80 di respiro sotto)
 *
 * Risultato: zero bande nere/cream sopra o sotto, tutto il canvas è
 * lavorato come nel post 4:5 — solo più ariosa.
 */
import React from "react";

const APP_STORE_URL = "https://apps.apple.com/it/app/id6777127700";

const SERVICES = [
  { label: "Annuncio pronto", hint: "Dal vocale, già scritto da copiare." },
  { label: "Foto perfette", hint: "Luce, ordine, restyling, arredo virtuale." },
  { label: "Sito personale", hint: "La tua vetrina online, attiva subito.", featured: true },
  { label: "Post & Reel Instagram", hint: "Pronti col tuo logo, da pubblicare." },
  { label: "Stima online del cliente", hint: "Form sul tuo sito, contatto diretto." },
  { label: "Agenda vocale", hint: "Appuntamenti dettati a voce." },
  { label: "WhatsApp marketing", hint: "Messaggi e auguri ai tuoi clienti." },
  { label: "CRM contatti", hint: "Tutti i clienti e lo storico in un posto." },
  { label: "Pubblicazione portali", hint: "Estensione Chrome che compila Idealista e Immobiliare." },
];

export default function InstagramAppDownloadReel() {
  // iter v32 r48 · User M1456: il primo paint mostrava il canvas a scale 0.32
  // su sfondo bianco prima che `useEffect` portasse scale a 1.0 in clean mode.
  // Questo creava un "flash bianco" di ~1s nel video MP4. Init lo state in
  // modo cleanMode-aware così la primissima render è già a scale 1.
  const initialClean = (() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("clean") === "1";
  })();
  const [scale, setScale] = React.useState(initialClean ? 1 : 0.32);

  const cleanMode = React.useMemo(() => initialClean, [initialClean]);

  React.useEffect(() => {
    if (cleanMode) { setScale(1); return; }
    const fit = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const s = Math.min((w - 32) / 1080, (h - 100) / 1920);
      setScale(Math.max(0.2, Math.min(0.9, s)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [cleanMode]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: cleanMode ? "#FFFFFF" : "#0F0E0C",
        padding: cleanMode ? 0 : "32px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="ig-reel-page"
    >
      <style>{`
        @keyframes pronto-fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pronto-fadeUpSlow {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pronto-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pronto-accent {
          from { opacity: 0; transform: scaleX(0); }
          to { opacity: 1; transform: scaleX(1); }
        }
        @keyframes pronto-phoneIn {
          from { opacity: 0; transform: translateY(40px) scale(0.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pronto-phoneFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pronto-iconReveal {
          0% { opacity: 0; transform: scale(0.4); }
          60% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pronto-iconPulseAfter {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.025); }
        }
        @keyframes pronto-screenBgWash {
          0% { background: #0A0B09; }
          100% { background: transparent; }
        }
        .anim-up {
          opacity: 0;
          animation: pronto-fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .anim-up-slow {
          opacity: 0;
          animation: pronto-fadeUpSlow 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .anim-in {
          opacity: 0;
          animation: pronto-fadeIn 0.7s ease-out forwards;
        }
        .anim-accent {
          opacity: 0;
          transform: scaleX(0);
          transform-origin: left center;
          animation: pronto-accent 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .phone-entrance {
          opacity: 0;
          animation: pronto-phoneIn 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .phone-float {
          animation: pronto-phoneFloat 5s ease-in-out 2.4s infinite;
        }
        .app-icon-reveal {
          opacity: 0;
          transform-origin: center;
          animation: pronto-iconReveal 0.95s cubic-bezier(0.34, 1.56, 0.64, 1) 1.9s forwards;
        }
        .app-icon-pulse-after {
          animation: pronto-iconPulseAfter 3.2s ease-in-out 3.2s infinite;
          transform-origin: center;
        }
        .app-screen-wash {
          background: #0A0B09;
          animation: pronto-screenBgWash 0.6s ease-out 1.55s forwards;
        }
        .app-label-reveal {
          opacity: 0;
          animation: pronto-fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 2.4s forwards;
        }
      `}</style>

      <div
        style={{
          width: 1080 * scale,
          height: 1920 * scale,
          position: "relative",
          overflow: "hidden",
          borderRadius: cleanMode ? 0 : 8,
          boxShadow: cleanMode ? "none" : "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div
          data-testid="ig-reel-canvas"
          className="relative overflow-hidden"
          style={{
            width: 1080,
            height: 1920,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            fontFamily: "'Inter','Helvetica Neue',sans-serif",
            background: "#F5F0E2",
          }}
        >
          {/* ───── Texture · paper grain (riempie 1080×1920) ───── */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: 0.07,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
          {/* Soft sage halo top-right · più grande per riempire 9:16 */}
          <div
            aria-hidden
            className="absolute"
            style={{
              top: -260,
              right: -220,
              width: 760,
              height: 760,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at center, rgba(124,139,110,0.22) 0%, rgba(124,139,110,0) 70%)",
            }}
          />
          {/* Soft sage halo mid-left · extra per riempire l'altezza */}
          <div
            aria-hidden
            className="absolute"
            style={{
              top: 720,
              left: -240,
              width: 560,
              height: 560,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at center, rgba(124,139,110,0.10) 0%, rgba(124,139,110,0) 70%)",
            }}
          />
          {/* Soft caramel halo bottom-left */}
          <div
            aria-hidden
            className="absolute"
            style={{
              bottom: -340,
              left: -240,
              width: 760,
              height: 760,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at center, rgba(181,124,94,0.16) 0%, rgba(181,124,94,0) 70%)",
            }}
          />

          {/* ═══════════════════════════════════════════
              HEADER · identico ai post IG (top 120)
              ═══════════════════════════════════════════ */}
          <div className="absolute" style={{ top: 120, left: 60, right: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="flex items-center gap-3" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src="/logo/mark-symbol-256.png"
                alt=""
                style={{ width: 48, height: 48, objectFit: "contain" }}
              />
              <div
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 30,
                  fontWeight: 700,
                  color: "#1C1C1A",
                  letterSpacing: "-0.015em",
                  lineHeight: 1,
                }}
              >
                Pronto
              </div>
            </div>
            <div
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 17,
                fontWeight: 500,
                color: "#1C1C1A",
                letterSpacing: "-0.005em",
                textAlign: "right",
                lineHeight: 1.25,
              }}
            >
              Un&apos;unica app
              <br />
              <span style={{ color: "#5A5A52", fontWeight: 400 }}>
                per l&apos;agente immobiliare
              </span>
            </div>
          </div>

          {/* Hairline divider · top 196 */}
          <div
            aria-hidden
            className="absolute"
            style={{
              top: 196,
              left: 60,
              right: 60,
              height: 1,
              background: "rgba(28,28,26,0.16)",
            }}
          />

          {/* ═══════════════════════════════════════════
              EYEBROW + ACCENT · top 260
              ═══════════════════════════════════════════ */}
          <div
            className="absolute anim-up"
            style={{ top: 260, left: 60, animationDelay: "0.1s" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "#B57C5E",
                  boxShadow: "0 0 0 5px rgba(181,124,94,0.16)",
                }}
              />
              <span
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.36em",
                  color: "#1C1C1A",
                  textTransform: "uppercase",
                }}
              >
                Scarica l&apos;app · iOS 17+
              </span>
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              TITOLO · HERO · "Da un vocale..." · top 320
              ═══════════════════════════════════════════ */}
          <div
            className="absolute"
            style={{ top: 320, left: 60, width: 700 }}
          >
            <h1
              className="anim-up"
              style={{
                margin: 0,
                fontFamily: "'Tinos','Times New Roman',serif",
                fontWeight: 400,
                fontSize: 96,
                lineHeight: 0.96,
                letterSpacing: "-0.03em",
                color: "#1C1C1A",
                animationDelay: "0.25s",
              }}
            >
              Da un{" "}
              <span style={{ fontStyle: "italic", color: "#7C8B6E" }}>
                vocale
              </span>
              <br />e qualche{" "}
              <span style={{ fontStyle: "italic", color: "#B57C5E" }}>
                scatto.
              </span>
            </h1>

            {/* Sub-claim caramel · "Al resto ci pensa Pronto." */}
            <div
              className="anim-up"
              style={{
                marginTop: 22,
                fontFamily: "'Tinos','Times New Roman',serif",
                fontStyle: "italic",
                fontSize: 40,
                lineHeight: 1.15,
                letterSpacing: "-0.005em",
                color: "#B57C5E",
                animationDelay: "0.55s",
              }}
            >
              Al resto ci pensa{" "}
              <span
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontStyle: "normal",
                  fontWeight: 700,
                  color: "#7C8B6E",
                  letterSpacing: "0.005em",
                }}
              >
                Pronto
              </span>
              .
            </div>

            {/* Accent line caramel */}
            <div
              className="anim-accent"
              style={{
                marginTop: 32,
                width: 130,
                height: 4,
                background: "#B57C5E",
                borderRadius: 2,
                animationDelay: "0.85s",
              }}
            />

            {/* Body description compatta */}
            <p
              className="anim-up"
              style={{
                marginTop: 28,
                fontSize: 20,
                lineHeight: 1.5,
                color: "#1C1C1A",
                maxWidth: 560,
                margin: "28px 0 0 0",
                animationDelay: "1.05s",
              }}
            >
              Tutto quello che oggi fai con cinque programmi diversi,
              <br />
              <span style={{ fontWeight: 700 }}>
                qui lo fai in un&apos;unica app.
              </span>
            </p>
          </div>

          {/* ═══════════════════════════════════════════
              9 BULLET SERVIZI · top 990 · sotto descrizione, sx
              ═══════════════════════════════════════════ */}
          <div
            className="absolute"
            style={{ top: 990, left: 60, width: 680 }}
          >
            <div
              className="anim-in"
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.36em",
                color: "#5A6A4D",
                textTransform: "uppercase",
                marginBottom: 22,
                animationDelay: "1.2s",
              }}
            >
              · Cosa fa, esattamente
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              {SERVICES.map((s, idx) => (
                <div
                  key={s.label}
                  className="anim-up-slow"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                    animationDelay: `${1.4 + idx * 0.16}s`,
                  }}
                >
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: 999,
                      background: s.featured ? "#B57C5E" : "#7C8B6E",
                      flexShrink: 0,
                      marginTop: 9,
                      boxShadow: s.featured
                        ? "0 0 0 5px rgba(181,124,94,0.14)"
                        : "0 0 0 5px rgba(124,139,110,0.12)",
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: "'Montserrat','Inter',sans-serif",
                        fontSize: 21,
                        fontWeight: 700,
                        color: "#1C1C1A",
                        letterSpacing: "-0.01em",
                        lineHeight: 1.2,
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        color: "#5A5A52",
                        lineHeight: 1.3,
                        marginTop: 3,
                      }}
                    >
                      {s.hint}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              iPhone 17 Pro · DESTRA · top 560
              (più ariosa nel 9:16: il telefono "respira" sopra)
              ═══════════════════════════════════════════ */}
          <div
            className="absolute phone-entrance"
            style={{
              top: 560,
              right: 50,
              width: 340,
              height: 720,
              transformOrigin: "center center",
              animationDelay: "0.6s",
              filter:
                "drop-shadow(0 28px 50px rgba(28,28,26,0.20)) drop-shadow(0 10px 18px rgba(28,28,26,0.12))",
            }}
          >
            <div
              className="phone-float"
              style={{ width: "100%", height: "100%" }}
            >
              {/* Telaio titanio Natural · ultra-thin */}
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 70,
                  background:
                    "linear-gradient(135deg, #B5AB94 0%, #968F7A 25%, #6E6856 60%, #3F3B30 100%)",
                  padding: 4,
                  position: "relative",
                  boxShadow:
                    "inset 0 0 0 1.5px rgba(255,255,255,0.12), inset 0 2px 8px rgba(255,255,255,0.08), inset 0 -4px 14px rgba(0,0,0,0.45)",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 66,
                    background: "#000",
                    padding: 3,
                    position: "relative",
                  }}
                >
                  {/* Pulsanti laterali iPhone 17 Pro */}
                  <div aria-hidden style={{ position: "absolute", left: -6, top: 124, width: 3, height: 34, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", left: -6, top: 174, width: 3, height: 100, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", right: -6, top: 204, width: 3, height: 84, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", right: -6, top: 304, width: 3, height: 26, background: "linear-gradient(180deg, #B5AB94 0%, #6E6856 100%)", borderRadius: 1.5, boxShadow: "0 0 0 1px rgba(0,0,0,0.4)" }} />

                  {/* Schermo · cream */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 63,
                      overflow: "hidden",
                      background:
                        "linear-gradient(180deg, #F4F1E8 0%, #EDE9DC 50%, #E5E0CF 100%)",
                      position: "relative",
                    }}
                  >
                    {/* Dynamic Island */}
                    <div
                      style={{
                        position: "absolute",
                        top: 18,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 116,
                        height: 34,
                        background: "#000",
                        borderRadius: 17,
                        zIndex: 6,
                      }}
                    />

                    {/* Status bar */}
                    <div
                      style={{
                        position: "absolute",
                        top: 22,
                        left: 24,
                        right: 24,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1C1C1A",
                        fontFamily:
                          "'SF Pro Display','Helvetica Neue',sans-serif",
                        letterSpacing: "-0.01em",
                        zIndex: 4,
                      }}
                    >
                      <span>9:41</span>
                      <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <svg width="16" height="10" viewBox="0 0 18 11" fill="none">
                          <rect x="0" y="7" width="3" height="4" rx="0.5" fill="#1C1C1A"/>
                          <rect x="5" y="5" width="3" height="6" rx="0.5" fill="#1C1C1A"/>
                          <rect x="10" y="2" width="3" height="9" rx="0.5" fill="#1C1C1A"/>
                          <rect x="15" y="0" width="3" height="11" rx="0.5" fill="#1C1C1A" opacity="0.4"/>
                        </svg>
                        <svg width="14" height="10" viewBox="0 0 16 11" fill="none">
                          <path d="M8 11 L6 9 a3 3 0 0 1 4 0 Z" fill="#1C1C1A"/>
                          <path d="M8 7 a5 5 0 0 1 4 1.5" stroke="#1C1C1A" strokeWidth="1.5" fill="none"/>
                          <path d="M8 3 a9 9 0 0 1 7 2.5" stroke="#1C1C1A" strokeWidth="1.5" fill="none"/>
                        </svg>
                        <svg width="22" height="11" viewBox="0 0 26 12" fill="none">
                          <rect x="0.5" y="0.5" width="22" height="11" rx="2.5" stroke="#1C1C1A" strokeWidth="1" fill="none"/>
                          <rect x="2" y="2" width="19" height="8" rx="1.2" fill="#1C1C1A"/>
                          <rect x="23" y="4" width="2" height="4" rx="0.8" fill="#1C1C1A"/>
                        </svg>
                      </span>
                    </div>

                    {/* App icon centrato · sage tile + P bianca */}
                    <div
                      className="app-icon-reveal"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginLeft: "-77px",
                        marginTop: "-86px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 18,
                      }}
                    >
                      <div
                        className="app-icon-pulse-after"
                        style={{
                          width: 154,
                          height: 154,
                          borderRadius: 38,
                          background:
                            "linear-gradient(155deg, #8A9678 0%, #7C8B6E 50%, #6B7A5D 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow:
                            "0 20px 46px rgba(38,52,33,0.32), 0 6px 14px rgba(38,52,33,0.22), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -2px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        <img
                          src="/logo/mark-symbol-white.png"
                          alt=""
                          style={{ width: 108, height: 108, objectFit: "contain" }}
                        />
                      </div>
                      <div
                        className="app-label-reveal"
                        style={{
                          fontFamily:
                            "'SF Pro Display','Helvetica Neue',sans-serif",
                          fontSize: 20,
                          fontWeight: 500,
                          color: "#1C1C1A",
                          letterSpacing: "-0.005em",
                        }}
                      >
                        Pronto
                      </div>
                    </div>

                    {/* Home indicator */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 15,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 134,
                        height: 4,
                        background: "#1C1C1A",
                        borderRadius: 2,
                        opacity: 0.6,
                        zIndex: 6,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              FOOTER · App Store · bottom 130
              ═══════════════════════════════════════════ */}
          <div
            className="absolute anim-up"
            style={{
              bottom: 130,
              left: 60,
              right: 60,
              paddingTop: 26,
              borderTop: "1px solid rgba(28,28,26,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              animationDelay: "2.0s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <a
                href={APP_STORE_URL}
                style={{ display: "inline-block", textDecoration: "none" }}
              >
                <img
                  src="/badges/app-store-it.svg"
                  alt="Scarica su App Store"
                  style={{ width: 280, height: "auto", display: "block" }}
                />
              </a>
              <div>
                <div
                  style={{
                    fontFamily: "'Montserrat','Inter',sans-serif",
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#1C1C1A",
                    letterSpacing: "-0.005em",
                    lineHeight: 1.15,
                  }}
                >
                  Scarica gratis
                </div>
                <div
                  style={{
                    fontSize: 16,
                    color: "#6B6A65",
                    marginTop: 3,
                  }}
                >
                  In promo di lancio · senza carta
                </div>
              </div>
            </div>
            <div
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 22,
                fontWeight: 700,
                color: "#1C1C1A",
                letterSpacing: "-0.005em",
              }}
            >
              prontoimmo.it
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
